import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { useListState } from '../../lib/useList';
import { ErrorPanel } from '../../components/ErrorPanel';
import { Badge, Button, CheckboxField, EmptyRow, FilterBar, PageHeader, Pager, SelectField, SortHeader, Spinner, TableWrap, TextField, Th } from '../../components/ui';
import { formatDate } from './dates';
import { INVOICE_STATUSES, InvoiceList, PAGE_SIZE, PLAT, invoiceStatusLabel, invoiceStatusTone, keys } from './api';
import { InvoiceDrawer } from './InvoiceDrawer';
import { BillingTabs, Money } from './parts';

type Status = '' | (typeof INVOICE_STATUSES)[number];

/** Platform: all invoices and credit notes with filters; drafts are reviewed and issued from the detail panel. */
export function InvoicesPage() {
  const [params, setParams] = useSearchParams();
  const tenantId = params.get('tenantId') ?? '';
  const list = useListState({ sort: 'issuedOn', order: 'desc' });
  const [status, setStatus] = useState<Status>('');
  const [kind, setKind] = useState<'' | 'invoice' | 'credit_note'>('');
  const [overdue, setOverdue] = useState(false);
  const [open, setOpen] = useState<string | null>(null);

  const query = { page: list.page, pageSize: PAGE_SIZE, search: list.search, sort: list.sort, order: list.order, tenantId, status, kind, overdue: overdue || undefined };
  const q = useQuery({
    queryKey: keys.invoices(query),
    queryFn: () => api.get(`${PLAT}/invoices`, { query, schema: InvoiceList }),
    placeholderData: keepPreviousData,
  });
  const sortProps = { sort: list.sort, order: list.order, onSort: list.toggleSort };
  return (
    <>
      <PageHeader
        title={t('billingUi.invoices.title')}
        description={t('billingUi.invoices.description')}
        actions={
          <Button
            onClick={() => {
              setStatus('draft');
              list.resetPage();
            }}
          >
            {t('billingUi.invoices.reviewDrafts')}
          </Button>
        }
      />
      <BillingTabs />
      <FilterBar columns="sm:grid-cols-4">
        <TextField label={t('common.search')} hint={t('billingUi.invoices.searchHint')} type="search" value={list.search} onChange={(e) => list.setSearch(e.target.value)} />
        <SelectField
          label={t('common.status')}
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as Status);
            list.resetPage();
          }}
        >
          <option value="">{t('billingUi.invoices.allStatuses')}</option>
          {INVOICE_STATUSES.map((s) => (
            <option key={s} value={s}>
              {invoiceStatusLabel(s)}
            </option>
          ))}
        </SelectField>
        <SelectField
          label={t('billingUi.invoices.kind')}
          value={kind}
          onChange={(e) => {
            setKind(e.target.value as typeof kind);
            list.resetPage();
          }}
        >
          <option value="">{t('billingUi.invoices.allKinds')}</option>
          <option value="invoice">{t('billingUi.kind.invoice')}</option>
          <option value="credit_note">{t('billingUi.kind.credit_note')}</option>
        </SelectField>
        <div className="flex items-end pb-1">
          <CheckboxField
            label={t('billingUi.invoices.overdueOnly')}
            checked={overdue}
            onChange={(e) => {
              setOverdue(e.target.checked);
              list.resetPage();
            }}
          />
        </div>
      </FilterBar>
      {tenantId ? (
        <p className="mb-3 text-sm">
          {t('billingUi.invoices.filteredToCustomer')}{' '}
          <Button
            onClick={() => {
              const next = new URLSearchParams(params);
              next.delete('tenantId');
              setParams(next);
              list.resetPage();
            }}
          >
            {t('billingUi.invoices.clearCustomer')}
          </Button>
        </p>
      ) : null}
      {q.isPending ? (
        <Spinner />
      ) : q.isError ? (
        <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />
      ) : (
        <>
          <TableWrap caption={t('billingUi.invoices.table')}>
            <thead className="border-b border-slate-200 bg-slate-50">
              <tr>
                <SortHeader label={t('billingUi.col.number')} field="number" {...sortProps} />
                <SortHeader label={t('billingUi.col.customer')} field="tenantName" {...sortProps} />
                <Th>{t('billingUi.invoices.kind')}</Th>
                <SortHeader label={t('billingUi.col.issued')} field="issuedOn" {...sortProps} />
                <Th>{t('billingUi.col.due')}</Th>
                <SortHeader label={t('billingUi.col.total')} field="total" {...sortProps} />
                <Th>{t('billingUi.col.balance')}</Th>
                <Th>{t('common.status')}</Th>
              </tr>
            </thead>
            <tbody>
              {q.data.items.length === 0 ? <EmptyRow cols={8} /> : null}
              {q.data.items.map((i) => {
                const label = i.number ?? t('billingUi.invoice.draftTitle');
                return (
                  <tr key={i.id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <button type="button" className="font-medium text-brand hover:underline" aria-label={t('billingUi.invoices.open', { label, name: i.tenantName })} onClick={() => setOpen(i.id)}>
                        {label}
                      </button>
                    </td>
                    <td className="px-4 py-3">{i.tenantName}</td>
                    <td className="px-4 py-3">{t(`billingUi.kind.${i.kind}`)}</td>
                    <td className="px-4 py-3">{formatDate(i.issuedOn)}</td>
                    <td className="px-4 py-3">{formatDate(i.dueOn)}</td>
                    <td className="px-4 py-3"><Money pence={i.total} /></td>
                    <td className="px-4 py-3"><Money pence={i.balance} /></td>
                    <td className="px-4 py-3"><Badge tone={invoiceStatusTone(i.status)}>{invoiceStatusLabel(i.status)}</Badge></td>
                  </tr>
                );
              })}
            </tbody>
          </TableWrap>
          <Pager page={list.page} pageSize={PAGE_SIZE} total={q.data.total} onPage={list.setPage} />
        </>
      )}
      {open ? <InvoiceDrawer id={open} onClose={() => setOpen(null)} /> : null}
    </>
  );
}
